import { useEffect, useState } from 'react'
// eslint-disable-next-line import/no-extraneous-dependencies
import axios from 'axios'
import { toast } from 'react-toastify'
import {
  getAnalyticsClientesPerdidos,
  getAnalyticsEvolucao,
  getAnalyticsMetricas,
  getAnalyticsVendedores,
} from '../../services/PredictAnalyticsService/getPredictAnalytics'
import { getVendedoresAll } from '../../services/SalesPeople/getSalesPeople'

export function usePredictAnalytics(pularRankingVendedores = false) {
  const [metricas, setMetricas] = useState<GetAnalyticsMetricasType>(
    {} as GetAnalyticsMetricasType
  )
  const [vendedores, setVendedores] = useState<GetAnalyticsVendedoresType>(
    {} as GetAnalyticsVendedoresType
  )
  const [clientesPerdidos, setClientesPerdidos] =
    useState<GetAnalyticsClientesPerdidosType>(
      {} as GetAnalyticsClientesPerdidosType
    )
  const [evolucao, setEvolucao] = useState<AnalyticsEvolucaoItemType[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingVendedores, setIsLoadingVendedores] = useState(false)
  const [isLoadingClientes, setIsLoadingClientes] = useState(false)

  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [vendedorId, setVendedorId] = useState<number | null>(null)
  const [listaVendedores, setListaVendedores] = useState<GetCompanieAll>([])
  const [pageVendedores, setPageVendedores] = useState(1)
  const [pageClientes, setPageClientes] = useState(1)

  const fetchMetricasEvolucao = async (source: any) => {
    setIsLoading(true)
    const [resultMetricas, resultEvolucao] = await Promise.all([
      getAnalyticsMetricas(source, dataInicio, dataFim, vendedorId),
      getAnalyticsEvolucao(source, dataInicio, dataFim, vendedorId),
    ])
    if ('message' in resultMetricas) {
      // Cancelada (troca rapida de periodo): outra request ja esta em voo; nao mexe no loading.
      if (resultMetricas.message === 'cancelToken') {
        return
      }
      toast.error(resultMetricas.message)
    } else {
      setMetricas(resultMetricas)
    }
    if (!('message' in resultEvolucao)) {
      setEvolucao(resultEvolucao)
    }
    setIsLoading(false)
  }

  const fetchVendedores = async (source: any) => {
    // Com filtro por vendedor o ranking colapsa para 1 linha: o card e ocultado na tela,
    // entao nem buscamos. Ao limpar o filtro (vendedorId null) o efeito recarrega.
    // Mesma razao para o perfil VENDEDOR, cujo escopo tem um vendedor so — o card tambem
    // e ocultado la, e sem isto a requisicao sairia sem destino a cada carga da tela. O
    // valor vem por PARAMETRO, e nao de um `useTopUserData()` aqui: o hook nao e Context
    // nem tem cache, entao cada instancia custa 2 GETs, e a tela ja resolveu esse enum.
    if (vendedorId || pularRankingVendedores) {
      setVendedores({} as GetAnalyticsVendedoresType)
      setIsLoadingVendedores(false)
      return
    }
    setIsLoadingVendedores(true)
    const result = await getAnalyticsVendedores(
      source,
      dataInicio,
      dataFim,
      pageVendedores
    )
    if ('message' in result) {
      if (result.message === 'cancelToken') {
        return
      }
      toast.error(result.message)
    } else {
      setVendedores(result)
    }
    setIsLoadingVendedores(false)
  }

  const fetchClientes = async (source: any) => {
    setIsLoadingClientes(true)
    const result = await getAnalyticsClientesPerdidos(
      source,
      dataInicio,
      dataFim,
      pageClientes,
      vendedorId
    )
    if ('message' in result) {
      if (result.message === 'cancelToken') {
        return
      }
      toast.error(result.message)
    } else {
      setClientesPerdidos(result)
    }
    setIsLoadingClientes(false)
  }

  // Lista de vendedores para o seletor (carrega uma vez). Mesma fonte das telas de Previsoes/Vendas.
  useEffect(() => {
    const carregarVendedores = async () => {
      const result = await getVendedoresAll('')
      if (Array.isArray(result)) {
        setListaVendedores(result)
      } else {
        toast.error(result.message)
      }
    }
    carregarVendedores()
  }, [])

  useEffect(() => {
    const source = axios.CancelToken.source()
    fetchMetricasEvolucao(source)
    return () => source.cancel('cancelToken')
  }, [JSON.stringify({ dataInicio, dataFim, vendedorId })])

  useEffect(() => {
    const source = axios.CancelToken.source()
    fetchVendedores(source)
    return () => source.cancel('cancelToken')
  }, [
    JSON.stringify({
      dataInicio,
      dataFim,
      pageVendedores,
      vendedorId,
      pularRankingVendedores,
    }),
  ])

  useEffect(() => {
    const source = axios.CancelToken.source()
    fetchClientes(source)
    return () => source.cancel('cancelToken')
  }, [JSON.stringify({ dataInicio, dataFim, pageClientes, vendedorId })])

  // Mudanca de periodo volta os dois rankings para a primeira pagina.
  const onChangeDataInicio = (value: string) => {
    setDataInicio(value)
    setPageVendedores(1)
    setPageClientes(1)
  }
  const onChangeDataFim = (value: string) => {
    setDataFim(value)
    setPageVendedores(1)
    setPageClientes(1)
  }
  // Troca de vendedor tambem reseta a paginacao dos rankings.
  const onChangeVendedor = (value: number | null) => {
    setVendedorId(value)
    setPageVendedores(1)
    setPageClientes(1)
  }

  return {
    metricas,
    vendedores,
    clientesPerdidos,
    evolucao,
    listaVendedores,
    vendedorId,
    // Expostos para o drill (PD-0006) herdar EXATAMENTE o mesmo recorte do painel — é o que
    // faz o total da lista bater com o número do card.
    dataInicio,
    dataFim,
    isLoading,
    isLoadingVendedores,
    isLoadingClientes,
    pageVendedores,
    setPageVendedores,
    pageClientes,
    setPageClientes,
    onChangeDataInicio,
    onChangeDataFim,
    onChangeVendedor,
  }
}
